import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  Text,
  Alert,
  Platform,
  Linking,
  Dimensions,
} from 'react-native';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import { LinearGradient } from 'expo-linear-gradient';
import { H2, Caption } from '../../components/common/TextStyles';
import { useTheme, getShadow } from '../../theme/useTheme';
import * as lmsService from '../../services/lmsService';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const LMS_BASE_URL = 'https://web.intuitlms.online';

const IntuitLMSScreen = ({ navigation, route }) => {
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const webViewRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [forceLogout, setForceLogout] = useState(false);

  // ── Sync check on mount ──────────────────────────────────────────────────
  React.useEffect(() => {
    const checkLogoutNeeded = async () => {
      const needed = await lmsService.isPortalLogoutNeeded();
      if (needed) {
        console.log('[IntuitLMS] Forced portal logout detected, clearing session...');
        setForceLogout(true);
        // Clear flag immediately so we don't loop
        await lmsService.clearPortalLogoutFlag();
      }
    };
    checkLogoutNeeded();
  }, []);

  // ── URL resolution ──────────────────────────────────────────────────────
  const getInitialUrl = () => {
    const { screen, institutionId, courseId, courseUrl } = route.params || {};
    if (courseUrl) return courseUrl;
    if (screen === 'select-institution') return `${LMS_BASE_URL}/select-institution`;
    if (screen === 'institutions') return `${LMS_BASE_URL}/institutions`;
    if (screen === 'courses' && institutionId) return `${LMS_BASE_URL}/institutions/${institutionId}/courses`;
    if (screen === 'course' && courseId) return `${LMS_BASE_URL}/courses/${courseId}`;
    if (screen === 'units') return `${LMS_BASE_URL}/units`;

    // Default: go straight to institution selection UI for logins/new users
    return `${LMS_BASE_URL}/select-institution`;
  };

  const initialUrl = getInitialUrl();

  // ── Auth Bridge Script ───────────────────────────────────────────────────
  const bridgeScript = `
    (function() {
      // Force logout if requested by app
      if (${forceLogout}) {
        localStorage.clear();
        console.log('[LMSBridge] LocalStorage cleared due to app logout');
      }

      function checkAuth() {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        if (token && user) {
          try {
            const userData = JSON.parse(user);
            if (userData && userData.id) {
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'LMS_AUTH',
                token: token,
                userId: userData.id
              }));
            }
          } catch (e) {}
        }
      }
      // Periodically check for auth data in portal's localStorage
      checkAuth();
      setInterval(checkAuth, 3000);
    })();
  `;

  const onMessage = async (event) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === 'LMS_AUTH') {
        const saved = await lmsService.saveLMSAuth(data.token, data.userId);
        if (saved) {
          console.log('[IntuitLMSScreen] LMS Auth synced to native app');
        }
      }
    } catch (e) {
      // Ignore non-JSON or other messages
    }
  };

  // ── Navigation ───────────────────────────────────────────────────────────
  const handleNavigationStateChange = (navState) => {
    setCanGoBack(navState.canGoBack);
    setCanGoForward(navState.canGoForward);
    setCurrentUrl(navState.url || '');
    if (!navState.loading) setLoading(false);
  };

  const handleGoBack = () => {
    if (webViewRef.current && canGoBack) {
      webViewRef.current.goBack();
    } else {
      navigation.goBack();
    }
  };

  const handleShouldStartLoadWithRequest = (request) => {
    const { url } = request;
    // Intercept transcript downloads and use native downloader which includes Auth tokens
    if (url && url.includes('/transcript') && url.includes('intuitlms.online')) {
      const matchUserId = url.match(/\/students\/(\d+)\/transcript/);
      if (matchUserId) {
        setLoading(true);
        lmsService.downloadTranscript({ courseName: 'Portal-Download' })
          .then((res) => {
             setLoading(false);
             if (!res.success) {
               Alert.alert('Download Failed', 'Could not download the transcript. Please try from your LMS journey page.');
             }
          })
          .catch(() => setLoading(false));
      } else {
         // Fallback if URL signature doesn't match exactly but contains transcript
         Linking.openURL(url).catch(() => {});
      }
      return false; // Stop webview from rendering the PDF blob
    }
    return true; // Let everything else load in webview
  };

  const handleGoForward = () => {
    if (webViewRef.current && canGoForward) webViewRef.current.goForward();
  };

  const handleRefresh = () => {
    setLoading(true);
    if (webViewRef.current) webViewRef.current.reload();
  };

  const cleanUrl = (url) => {
    if (!url) return 'intuitlms.online';
    const match = url.match(/:\/\/(.[^/]+)/);
    return match ? match[1] : 'intuitlms.online';
  };

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Premium Header */}
      <View style={[styles.header, { paddingTop: insets.top }]}>
        <LinearGradient colors={['#1e1b4b', '#4338ca']} style={StyleSheet.absoluteFill} />
        <View style={styles.headerContent}>
          <TouchableOpacity onPress={handleGoBack} style={styles.backBtn}>
            <Ionicons name="chevron-back" size={22} color="#FFFFFF" />
          </TouchableOpacity>

          <View style={styles.titleArea}>
            <H2 style={styles.titleText}>Intuit LMS</H2>
            <View style={styles.secureBadge}>
              <Ionicons name="lock-closed" size={10} color="#10B981" />
              <Caption style={styles.secureText}>{cleanUrl(currentUrl)}</Caption>
            </View>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              onPress={() => navigation.navigate('LMSProgress')}
              style={[styles.actionCircle, { marginRight: 8 }]}
            >
              <Ionicons name="bar-chart-outline" size={16} color="#FFFFFF" />
            </TouchableOpacity>
            <TouchableOpacity onPress={handleRefresh} style={styles.actionCircle}>
              <Ionicons name="refresh" size={18} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <WebView
        ref={webViewRef}
        source={{
          uri: initialUrl,
        }}
        style={styles.webView}
        onLoadStart={() => setLoading(true)}
        onLoadEnd={() => setLoading(false)}
        onNavigationStateChange={handleNavigationStateChange}
        onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
        injectedJavaScript={bridgeScript}
        onMessage={onMessage}
        javaScriptEnabled={true}
        domStorageEnabled={true}
        startInLoadingState={true}
        scalesPageToFit={true}
        allowsBackForwardNavigationGestures={true}
        renderLoading={() => (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#6366F1" />
          </View>
        )}
        onError={() => {
          Alert.alert(
            'Connection Error',
            'Unable to load the LMS portal. Please check your internet connection.',
            [
              { text: 'Retry', onPress: handleRefresh },
              { text: 'Go Back', onPress: () => navigation.goBack() },
            ]
          );
        }}
      />



      {/* Loading Overlay */}
      {loading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#6366F1" />
          <Caption style={styles.loadTxt}>Loading Learning Portal...</Caption>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    height: 100,
    justifyContent: 'flex-end',
    ...getShadow('sm'),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 15,
    gap: 15,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleArea: {
    flex: 1,
  },
  titleText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '800',
  },
  secureBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  secureText: {
    color: '#10B981',
    fontSize: 9,
    fontWeight: '700',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionCircle: {
    width: 36,
    height: 36,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  webView: {
    flex: 1,
  },
  bottomNav: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    gap: 10,
  },
  navBtn: {
    padding: 8,
  },
  btnDisabled: {
    opacity: 0.3,
  },
  divider: {
    flex: 1,
  },
  progressBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    marginRight: 6,
  },
  progressBtnText: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 10,
  },
  browserBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#F5F3FF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
  },
  browserText: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 10,
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255,255,255,0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  loadTxt: {
    marginTop: 15,
    color: '#6B7280',
    letterSpacing: 0.5,
  },
});

export default IntuitLMSScreen;
